import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Chip } from "@mui/material";
import { Visibility, AttachMoney, Person, MenuBook, Schedule, People } from "@mui/icons-material";

import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import Loader from "components/loader";
import PageHeader from "./components/PageHeader";
import EmptyStateCard from "./components/EmptyStateCard";
import CourseStatusChip from "./components/CourseStatusChip";
import CourseCardShell from "./components/CourseCardShell";
import CourseMetaItem from "./components/CourseMetaItem";
import { formatCoursePrice, getModuleCount } from "./utils/courseFormatters";

import { useCourse } from "hooks/useCourse";
import type { Course } from "slices/courseSlice";
import { COMPCHECK_URL } from "configs/api";

function ExploreCourses(): React.JSX.Element {
  const navigate = useNavigate();
  const { courses, loading, getAllCourses } = useCourse();
  const [page] = useState(1);

  useEffect(() => {
    getAllCourses(page, 24, undefined, undefined, "explore");
  }, [page, getAllCourses]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3} {...({} as any)}>
        <PageHeader
          title="Explore Courses"
          subtitle="Discover creator courses. Paid courses require purchase before full company access."
          mb={2}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" py={8} {...({} as any)}>
            <Loader loading={true} {...({} as any)} />
          </Box>
        ) : courses.length === 0 ? (
          <EmptyStateCard title="No courses found" />
        ) : (
          <Grid container spacing={3}>
            {courses.map((course: Course) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={course.id}>
                <CourseCardShell onClick={() => navigate(`/courses/${course.id}`)}>
                    {course.thumbnailURL && (
                      <Box
                        sx={{
                          width: "100%",
                          height: 160,
                          mb: 2,
                          borderRadius: 1,
                          overflow: "hidden",
                          backgroundColor: "grey.100",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        {...({} as any)}
                      >
                        <img
                          src={`${COMPCHECK_URL}/api/${course.thumbnailURL}`}
                          alt={course.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "fill",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      </Box>
                    )}

                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1.5} {...({} as any)}>
                      <CourseStatusChip status={course.status} size="small" />
                      <Chip
                        label={course.isFree ? "Free" : "Paid"}
                        size="small"
                        color={course.isFree ? "success" : "warning"}
                        variant="outlined"
                      />
                    </Box>

                    <Typography variant="h6" fontWeight="bold" mb={1} {...({} as any)}>
                      {course.title}
                    </Typography>
                    <Typography variant="body2" color="text" mb={2} {...({} as any)}>
                      {course.description || "No description available"}
                    </Typography>

                    <Box mb={1} {...({} as any)}>
                      <CourseMetaItem
                        icon={<Person fontSize="small" color="action" />}
                        text={course.instructorName || "Creator"}
                        truncate
                      />
                    </Box>

                    <Box mb={2} {...({} as any)}>
                      <CourseMetaItem
                        icon={<AttachMoney fontSize="small" color="action" />}
                        text={formatCoursePrice(course.price, course.isFree)}
                      />
                    </Box>

                    <Box mb={0.75} {...({} as any)}>
                      <CourseMetaItem
                        icon={<MenuBook fontSize="small" color="action" />}
                        text={`${getModuleCount(course.moduleCount, course.modules)} module${getModuleCount(course.moduleCount, course.modules) === 1 ? "" : "s"}`}
                      />
                    </Box>

                    <Box mb={0.75} {...({} as any)}>
                      <CourseMetaItem
                        icon={<Schedule fontSize="small" color="action" />}
                        text={`Total duration: ${Math.max(0, Number(course.totalDurationSeconds ?? course.totalDuration ?? 0))} sec`}
                      />
                    </Box>

                    <Box mb={2} {...({} as any)}>
                      <CourseMetaItem
                        icon={<People fontSize="small" color="action" />}
                        text={`${course.enrollmentCount ?? 0} enrollment${(course.enrollmentCount ?? 0) === 1 ? "" : "s"}`}
                      />
                    </Box>

                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/courses/${course.id}`);
                      }}
                      {...({} as any)}
                    >
                      <Visibility sx={{ mr: 0.5, fontSize: "16px" }} />
                      View Course
                    </Button>
                </CourseCardShell>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

export default ExploreCourses;
